// Scene Object System for Scene Editor
import { System } from '@/engine/systems/System';
import { logger } from '@/engine/logging';
import { sceneState, getAllObjects } from '../stores/sceneEditorStore';
import type { SceneObject, SpriteObject } from '../types';
import * as PIXI from 'pixi.js';
import { PixiSpriteRenderer } from '@/engine/components/PixiSpriteRenderer';
import { NeonGlowScanlineFilter } from '@/game/graphics/filters/NeonGlowScanlineFilter';
import { ServiceRegistry, ServiceKeys } from '@/engine/registry';
import { GameObject } from '@/engine/GameObject';
import { SpaceBackgroundSystem } from '@/game/systems/SpaceBackgroundSystem';

export class SceneObjectSystem extends System {
  private viewport?: PIXI.Container;
  private bgContainer?: PIXI.Container;
  private sceneObjects: Map<string, PixiSpriteRenderer> = new Map();
  private spaceBackgroundSystem?: SpaceBackgroundSystem;
  private bgWidth = 1920;
  private bgHeight = 1080;

  constructor(scene: any) {
    super(scene);
  }

  /**
   * Инициализация системы с необходимыми зависимостями
   */
  initialize(
    viewport: PIXI.Container,
    bgContainer: PIXI.Container,
    bgWidth: number,
    bgHeight: number
  ) {
    this.viewport = viewport;
    this.bgContainer = bgContainer;
    this.bgWidth = bgWidth;
    this.bgHeight = bgHeight;

    // Создаем SpaceBackgroundSystem
    this.spaceBackgroundSystem = new SpaceBackgroundSystem(this.scene, bgContainer);

    logger.info('🎨 Scene Object System initialized', {
      source: 'scene-object-system',
      viewport: !!this.viewport,
      bgContainer: !!this.bgContainer,
      bgWidth: this.bgWidth,
      bgHeight: this.bgHeight,
      spaceBackgroundSystem: !!this.spaceBackgroundSystem,
    });
  }

  protected onStart(): void {
    // Запускаем SpaceBackgroundSystem
    this.spaceBackgroundSystem?.start();

    // Загружаем существующие объекты после запуска системы
    this.loadExistingObjects();
  }

  protected onStop(): void {
    this.spaceBackgroundSystem?.stop();
    this.cleanup();
  }

  protected onUpdate(deltaTime: number): void {
    // Обновляем SpaceBackgroundSystem
    this.spaceBackgroundSystem?.update(deltaTime);

    // Обновляем все объекты сцены
    this.sceneObjects.forEach(renderer => {
      renderer.update(deltaTime);
    });

    // Обновляем фильтры (для анимированных эффектов)
    const state = sceneState.get();
    Object.values(state.objects).forEach(object => {
      if (object.type === 'sprite') {
        const renderer = this.sceneObjects.get(object.id);
        if (renderer) {
          const spriteRenderer = (renderer as any).spriteRenderer;
          if (spriteRenderer.sprite.filters) {
            spriteRenderer.sprite.filters.forEach((filter: any) => {
              if (filter.update) {
                filter.update(deltaTime);
              }
            });
          }
        }
      }
    });
  }

  protected onDestroy(): void {
    this.spaceBackgroundSystem?.destroy();
    this.cleanup();
  }

  private cleanup() {
    this.sceneObjects.forEach(renderer => renderer.destroy());
    this.sceneObjects.clear();
    this.spaceBackgroundSystem = undefined;
  }

  /**
   * Добавить объект в сцену
   */
  async addSceneObject(object: SceneObject) {
    logger.info('➕ Adding scene object', {
      source: 'scene-object-system',
      id: object.id,
      type: object.type,
      name: object.name,
    });

    if (object.type === 'sprite') {
      await this.createSpriteObject(object as SpriteObject);
    } else if (object.type === 'spaceBackground') {
      // spaceBackground теперь управляется SpaceBackgroundSystem
      logger.info('🌌 Space background managed by SpaceBackgroundSystem', {
        source: 'scene-object-system',
        id: object.id,
      });
    } else {
      logger.warn('⚠️ Unknown object type', {
        source: 'scene-object-system',
        id: object.id,
        type: object.type,
      });
    }
  }

  /**
   * Удалить объект из сцены
   */
  removeSceneObject(objectId: string) {
    const renderer = this.sceneObjects.get(objectId);
    if (renderer) {
      renderer.destroy();
      this.sceneObjects.delete(objectId);
    }
  }

  /**
   * Обновить объект в сцене
   */
  async updateSceneObject(object: SceneObject) {
    if (object.type === 'sprite') {
      await this.updateSpriteObject(object as SpriteObject);
    } else if (object.type === 'spaceBackground') {
      // spaceBackground теперь управляется SpaceBackgroundSystem
      logger.info('🌌 Space background managed by SpaceBackgroundSystem', {
        source: 'scene-object-system',
        id: object.id,
      });
    }
  }

  /**
   * Получить объект по ID
   */
  getSceneObject(objectId: string): PixiSpriteRenderer | null {
    return this.sceneObjects.get(objectId) || null;
  }

  /**
   * Найти объект по позиции
   */
  findObjectAtPosition(x: number, y: number): SceneObject | null {
    const state = sceneState.get();

    logger.info('🔍 Finding object at position', {
      source: 'scene-object-system',
      position: { x, y },
      objectsCount: Object.keys(state.objects).length,
      sceneObjectsCount: this.sceneObjects.size,
    });

    // Check objects in reverse order (top to bottom)
    const objects = Object.values(state.objects).sort((a, b) => {
      const aRenderer = this.sceneObjects.get(a.id);
      const bRenderer = this.sceneObjects.get(b.id);
      return (bRenderer?.sprite.zIndex || 0) - (aRenderer?.sprite.zIndex || 0);
    });

    for (const object of objects) {
      if (object.type === 'sprite') {
        const renderer = this.sceneObjects.get(object.id);
        if (renderer) {
          const bounds = renderer.sprite.getBounds();
          const isHit = this.isPointInSprite(x, y, renderer.sprite);

          logger.info('🎯 Checking sprite object', {
            source: 'scene-object-system',
            objectId: object.id,
            objectName: object.name,
            objectPosition: { x: object.x, y: object.y },
            spritePosition: { x: renderer.sprite.position.x, y: renderer.sprite.position.y },
            spriteBounds: bounds,
            clickPosition: { x, y },
            isHit,
          });

          if (isHit) {
            return object;
          }
        }
      }
    }

    return null;
  }

  private isPointInSprite(x: number, y: number, sprite: PIXI.Sprite): boolean {
    // Получаем локальные границы спрайта относительно его родителя (viewport)
    const localBounds = sprite.getLocalBounds();
    const spritePos = sprite.position;

    // Вычисляем границы в локальной системе координат viewport
    const left = spritePos.x + localBounds.x;
    const right = spritePos.x + localBounds.x + localBounds.width;
    const top = spritePos.y + localBounds.y;
    const bottom = spritePos.y + localBounds.y + localBounds.height;

    const isHit = x >= left && x <= right && y >= top && y <= bottom;

    logger.info('🎯 Point in sprite check', {
      source: 'scene-object-system',
      clickPos: { x, y },
      spritePos,
      localBounds,
      calculatedBounds: { left, right, top, bottom },
      isHit,
    });

    return isHit;
  }

  private async createSpriteObject(object: SpriteObject) {
    logger.info('🎨 Creating sprite object with ECS architecture', {
      source: 'scene-object-system',
      id: object.id,
      texture: object.texture,
      textureName: object.textureName,
      position: { x: object.x, y: object.y },
      scale: object.scale,
    });

    // Создаем текстуру
    let texture: PIXI.Texture;
    if (object.texture === 'placeholder') {
      // Создаем заглушку через Graphics
      const graphics = new PIXI.Graphics();
      const size = object.scale > 100 ? object.scale : 64;

      // Заглушка для пустой текстуры
      graphics.beginFill(0x000000, 0);
      graphics.drawRect(-size / 2, -size / 2, size, size);
      graphics.endFill();

      // Рамка для видимости
      graphics.lineStyle(2, object.tint, object.alpha);
      graphics.drawRect(-size / 2, -size / 2, size, size);

      // Текст "No Texture" в центре
      graphics.beginFill(object.tint, 0.3);
      graphics.drawRect(-size / 4, -8, size / 2, 16);
      graphics.endFill();

      // Конвертируем Graphics в текстуру
      const pixiApp = ServiceRegistry.get(ServiceKeys.PixiApp) as PIXI.Application;
      texture = pixiApp.renderer.generateTexture(graphics);
    } else {
      // Загружаем настоящую текстуру
      try {
        if (object.texture.startsWith('blob:')) {
          // Загружаем blob URL с правильным синтаксисом PIXI v8
          texture = await PIXI.Assets.load({
            src: object.texture,
            format: 'png',
            loadParser: 'loadTextures',
          });
        } else {
          texture = PIXI.Texture.from(object.texture);
        }

        logger.info('🖼️ Real texture loaded', {
          source: 'scene-object-system',
          id: object.id,
          textureUrl: object.texture,
          textureName: object.textureName,
          textureSize: { width: texture.width, height: texture.height },
        });
      } catch (error) {
        logger.error('❌ Failed to load texture', error as Error, {
          source: 'scene-object-system',
          id: object.id,
          textureUrl: object.texture,
        });
        // Fallback к белой текстуре
        texture = PIXI.Texture.WHITE;
      }
    }

    // Создаем PixiSpriteRenderer
    const spriteRenderer = new PixiSpriteRenderer(this.viewport!, {
      texture,
      anchor: object.anchor,
      tint: object.tint,
      alpha: object.alpha,
      visible: object.visible,
    });

    // Делаем спрайт интерактивным для событий мыши
    spriteRenderer.sprite.interactive = true;

    // Применяем фильтры если есть
    const filters = this.createFiltersFromConfig(object.filters);
    if (filters.length > 0) {
      spriteRenderer.setFilters(filters);
    }

    // Создаем GameObject для ECS
    const gameObject = new GameObject()
      .setName(object.name)
      .setPosition(object.x, object.y)
      .setRotation(object.rotation)
      .setScale(object.scale)
      .setActive(object.visible);

    // Добавляем компонент к GameObject
    gameObject.add(spriteRenderer);

    // Добавляем в сцену (это автоматически добавит спрайт в viewport)
    this.scene.add(gameObject);

    // Сохраняем в Map для управления
    this.sceneObjects.set(object.id, {
      gameObject,
      spriteRenderer,
      sprite: spriteRenderer.sprite, // для совместимости
      destroy: () => {
        this.scene.remove(gameObject);
        gameObject._onRemovedFromScene();
      },
      update: () => {
        // ECS автоматически обновляет компоненты
      },
    } as any);

    logger.info('✅ Sprite object created', {
      source: 'scene-object-system',
      id: object.id,
      spriteVisible: spriteRenderer.sprite.visible,
      sceneObjectsCount: this.sceneObjects.size,
    });
  }

  private async updateSpriteObject(object: SpriteObject) {
    const renderer = this.sceneObjects.get(object.id);
    if (!renderer) {
      logger.warn('⚠️ No renderer found for object', {
        source: 'scene-object-system',
        id: object.id,
        sceneObjectsCount: this.sceneObjects.size,
      });
      return;
    }

    const gameObject = (renderer as any).gameObject;
    const spriteRenderer = (renderer as any).spriteRenderer;

    logger.info('🔄 Updating sprite object with ECS', {
      source: 'scene-object-system',
      id: object.id,
      texture: object.texture,
      position: { x: object.x, y: object.y },
      scale: object.scale,
      visible: object.visible,
    });

    // Обновляем GameObject свойства
    gameObject.setName(object.name);
    gameObject.setPosition(object.x, object.y);
    gameObject.setRotation(object.rotation);
    gameObject.setScale(object.scale);
    gameObject.setActive(object.visible);

    // Обновляем SpriteRenderer свойства
    spriteRenderer.setTint(object.tint);
    spriteRenderer.setAlpha(object.alpha);
    spriteRenderer.setVisible(object.visible);

    // Обновляем фильтры
    this.updateObjectFilters(object);

    // Всегда обновляем текстуру
    try {
      let newTexture: PIXI.Texture;

      if (object.texture === 'placeholder') {
        // Если вернулись к placeholder, создаем заглушку
        const graphics = new PIXI.Graphics();
        const size = object.scale > 100 ? object.scale : 64;

        graphics.beginFill(0x000000, 0);
        graphics.drawRect(-size / 2, -size / 2, size, size);
        graphics.endFill();

        graphics.lineStyle(2, object.tint, object.alpha);
        graphics.drawRect(-size / 2, -size / 2, size, size);

        graphics.beginFill(object.tint, 0.3);
        graphics.drawRect(-size / 4, -8, size / 2, 16);
        graphics.endFill();

        const pixiApp = ServiceRegistry.get(ServiceKeys.PixiApp) as PIXI.Application;
        newTexture = pixiApp.renderer.generateTexture(graphics);

        logger.info('🔄 Applied placeholder texture', {
          source: 'scene-object-system',
          id: object.id,
        });
      } else if (object.texture.startsWith('blob:')) {
        // Для blob URL используем специальный подход
        newTexture = await PIXI.Assets.load({
          src: object.texture,
          format: 'png', // или определять по расширению файла
          loadParser: 'loadTextures',
        });

        logger.info('🖼️ Blob texture loaded via Assets', {
          source: 'scene-object-system',
          id: object.id,
          textureUrl: object.texture,
        });
      } else {
        // Для обычных URL используем стандартный метод
        newTexture = PIXI.Texture.from(object.texture);

        logger.info('🖼️ Regular texture loaded', {
          source: 'scene-object-system',
          id: object.id,
          textureUrl: object.texture,
        });
      }

      spriteRenderer.setTexture(newTexture);
      logger.info('🖼️ Texture updated in sprite renderer', {
        source: 'scene-object-system',
        id: object.id,
        textureUrl: object.texture,
        textureName: object.textureName,
        textureSize: { width: newTexture.width, height: newTexture.height },
      });
    } catch (error) {
      logger.error('❌ Failed to update texture', error as Error, {
        source: 'scene-object-system',
        id: object.id,
        textureUrl: object.texture,
      });
    }

    logger.info('🔄 Sprite object updated', {
      source: 'scene-object-system',
      id: object.id,
      position: { x: object.x, y: object.y },
      texture: object.texture,
    });
  }

  private createFiltersFromConfig(filters?: any[]): PIXI.Filter[] {
    if (!filters || filters.length === 0) return [];

    const pixiFilters: PIXI.Filter[] = [];

    for (const filterConfig of filters) {
      if (!filterConfig.enabled) continue;

      switch (filterConfig.type) {
        case 'neonGlowScanline':
          const neonFilter = new NeonGlowScanlineFilter({
            glowColor: filterConfig.glowColor,
            innerStrength: filterConfig.innerStrength,
            outerStrength: filterConfig.outerStrength,
            radius: filterConfig.radius,
            samples: filterConfig.samples,
            alphaThreshold: filterConfig.alphaThreshold,
            scanIntensity: filterConfig.scanIntensity,
            scanSpeed: filterConfig.scanSpeed,
          });
          pixiFilters.push(neonFilter);
          break;

        default:
          logger.warn('⚠️ Unknown filter type', {
            source: 'scene-object-system',
            filterType: filterConfig.type,
          });
      }
    }

    logger.info('🎨 Filters created', {
      source: 'scene-object-system',
      filtersCount: pixiFilters.length,
      filterTypes: filters.map(f => f.type),
    });

    return pixiFilters;
  }

  private updateObjectFilters(object: SpriteObject) {
    const renderer = this.sceneObjects.get(object.id);
    if (!renderer) return;

    const spriteRenderer = (renderer as any).spriteRenderer;
    const filters = this.createFiltersFromConfig(object.filters);

    spriteRenderer.setFilters(filters);

    logger.info('🎨 Object filters updated', {
      source: 'scene-object-system',
      id: object.id,
      filtersCount: filters.length,
    });
  }

  private loadExistingObjects() {
    const objects = getAllObjects();
    logger.info('🔄 Loading existing objects', {
      source: 'scene-object-system',
      objectsCount: objects.length,
      objects: objects.map(obj => ({ id: obj.id, type: obj.type, name: obj.name })),
    });

    objects.forEach(object => {
      this.addSceneObject(object);
    });
  }

  /**
   * Получить Map объектов сцены
   */
  get sceneObjectsMap(): Map<string, PixiSpriteRenderer> {
    return this.sceneObjects;
  }

  /**
   * Изменить размер фона
   */
  resizeBackground(width: number, height: number) {
    this.bgWidth = width;
    this.bgHeight = height;
    this.spaceBackgroundSystem?.resize(width, height);
  }
}
